import { SupportDetails } from "@/components/ui/copyable-reference";
import { PermissionDeniedState } from "@/components/ui/system-state";
import { ApiFailure } from "@/components/data-display/api-failure";
import type { AdminConsoleFailure } from "@/lib/admin-console-api";

/**
 * The failed-read state for every admin console page (ALL-5773). A 401/403 is a
 * permission state, never an error card; anything else is the shared ApiFailure
 * (error-locale copy, Retry and the server's support reference). The copy says which of the server's two
 * refusals applied, so an admin knows whether to ask for a role or whether this
 * console simply does not serve their organization.
 */
export function AdminConsoleFailureState({
  failure,
  subject,
  onRetry,
}: {
  failure: AdminConsoleFailure;
  /** What could not be read, lower case, e.g. "service health". */
  subject: string;
  onRetry?: () => void;
}) {
  if (failure.denial === "unauthenticated") {
    return (
      <div>
        <PermissionDeniedState
          title="Sign in again"
          description={`Your session ended, so ${subject} could not be loaded. Sign in again to continue.`}
        />
        <SupportDetails reference={failure.supportReference} className="mt-3" />
      </div>
    );
  }
  if (failure.denial === "admin_organization_mismatch") {
    return (
      <div>
        <PermissionDeniedState
          title="Not available for your organization"
          description={`${capitalize(subject)} covers the organization that operates this deployment. Your account belongs to a different organization, so there is nothing to show here.`}
        />
        <SupportDetails reference={failure.supportReference} className="mt-3" />
      </div>
    );
  }
  if (failure.denial) {
    return (
      <div>
        <PermissionDeniedState
          title="Access required"
          description={`You don't have access to ${subject}. It needs an administrator role with diagnostics, audit, user or readiness read access. Ask an organization admin to grant it.`}
          details={failure.capability ? [{ k: "Needs", v: failure.capability }] : undefined}
        />
        <SupportDetails reference={failure.supportReference} className="mt-3" />
      </div>
    );
  }
  // Any other failure goes through the shared inline failure (error-locale copy,
  // Retry, the sr- support reference); the raw reason code is never the message.
  return (
    <ApiFailure
      error={{ reasonCode: failure.reasonCode, supportReference: failure.supportReference }}
      subject={subject}
      onRetry={onRetry}
    />
  );
}

function capitalize(text: string): string {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

